import { CREATOR_FIELDS } from '../user/fragment';

export const VIDEOS_STAT = `
  query videosStat {
    videosStat {
      videoCreated
      videoCreatedByDay
      videoCreatedByWeek
    }
  }
`;

// Mobile home of a visitor (read only, the actions send to the app): same feed as on a computer.
export const GUEST_HOME_VIDEOS = `
  query guestHomeVideos($input: GuestVideoPaginationInput!) {
    guestVideosPagination(input: $input) {
      docs {
        codeshare
        createdAt
        id
        filename
        description
        kills
        mode
        likes
        favorites
        gameName
        gameId
        height
        categoryId
        categoryName
        comments
        top
        path
        stream
        shares
        platform
        user {
          avatar
          username
        }
        userId
        width
        weapon
      }
    }
  }
`;

export const THUMBNAIL_VIDEOS_PAGINATION = `
  query videosPagination($input: VideoPaginationInput) {
    videosPagination(input: $input) {
      docs {
        codeshare
        createdAt
        id
        filename
        description
        kills
        mode
        likes
        isFollowed
        gameName
        gameId
        height
        categoryId
        categoryName
        comments
        thumbnail
        top
        path
        shares
        platform
        user {
          email
          username
          twitter
        }
        userId
        views
        width
        weapon
      }
      totalDocs
      offset
      limit
      totalPages
      hasPrevPage
      hasNextPage
      page
      pagingCounter
      prevPage
      nextPage
    }
  }
`;

export const VIDEO = `
  query video($id: ID!) {
    video(id: $id) {
      createdAt
      codeshare
      id
      filename
      thumbnail
      description
      kills
      mode
      favorites
      likes
      isFollowed
      isFavorite
      isLiked
      isViewed
      game {
        id
        name
        modes
        weapons
      }
      gameName
      gameId
      height
      categoryId
      categoryName
      comments
      path
      platform
      shares
      top
      user {
        id
        email
        avatar
        username
        twitter
      }
      userId
      views
      width
      weapon
    }
  }
`;

// Admin notification detail: only what the player needs.
export const VIDEO_PLAYER = `
  query videoPlayer($id: ID!) {
    video(id: $id) {
      id
      path
      thumbnail
    }
  }
`;

export const VIDEO_BY_CODESHARE = `
  query videoByCodeshare($codeshare: String!) {
    videoByCodeshare(codeshare: $codeshare) {
      createdAt
      codeshare
      id
      filename
      description
      kills
      mode
      favorites
      likes
      isFollowed
      isFavorite
      isLiked
      isViewed
      game {
        id
        name
        modes
        weapons
      }
      gameName
      gameId
      height
      categoryId
      categoryName
      comments
      path
      platform
      shares
      stream
      top
      user {
        id
        email
        avatar
        username
        twitter
      }
      userId
      views
      width
      weapon
    }
  }
`;

export const VIDEOS_ADMIN_PAGINATION = `
  query videosAdminPagination($input: VideoAdminPaginationInput) {
    videosAdminPagination(input: $input) {
      docs {
        codeshare
        createdAt
        id
        description
        kills
        mode
        likes
        gameName
        comments
        thumbnail
        thumbnailInPng
        top
        path
        short
        platform
        user {
          ${CREATOR_FIELDS}
        }
        userId
        views
        weapon
      }
      totalDocs
      offset
      limit
      hasNextPage
    }
  }
`;

// Fields shown by the feed player.
const FEED_VIDEO_FIELDS = `
      id
      codeshare
      comments
      createdAt
      description
      gameName
      gameId
      height
      isFollowed
      isFavorite
      isLiked
      isViewed
      kills
      favorites
      likes
      mode
      path
      platform
      shares
      stream
      thumbnail
      top
      user {
        id
        avatar
        rank
        username
      }
      userId
      views
      weapon
      weeklyRanking
      width
`;

// Home feed: the server excludes the videos already served (resetHome starts a new session).
export const FEED_VIDEOS = `
  query feedVideos($input: VideoV2PaginationInput) {
    videosV2Pagination(input: $input) {
      currentFeed
      docs {
        ${FEED_VIDEO_FIELDS}
      }
    }
  }
`;

// Feed of a visitor who is not logged in: only "Pour toi", the server remembers the installId.
export const GUEST_FEED_VIDEOS = `
  query guestFeedVideos($input: GuestVideoPaginationInput!) {
    guestVideosPagination(input: $input) {
      docs {
        ${FEED_VIDEO_FIELDS}
      }
    }
  }
`;

// Visitor: the videos of a ranking (public), for the list; the videos themselves are locked.
export const GUEST_RANKING_VIDEOS = `
  query guestRankingVideos($videoIds: [ID!]!) {
    guestRankingVideos(videoIds: $videoIds) {
      docs {
        ${FEED_VIDEO_FIELDS}
      }
    }
  }
`;

export const FEED_VIDEO = `
  query feedVideo($id: ID!) {
    video(id: $id) {
      ${FEED_VIDEO_FIELDS}
    }
  }
`;

// Shared link (/short/:codeshare) opened in the feed: public, a visitor gets it too.
export const FEED_VIDEO_BY_CODESHARE = `
  query feedVideoByCodeshare($codeshare: String!) {
    videoByCodeshare(codeshare: $codeshare) {
      ${FEED_VIDEO_FIELDS}
    }
  }
`;

// Videos of a weekly ranking (ranking + videoIds): each one has its rank in `top` (1 = best).
export const RANKING_VIDEOS = `
  query rankingVideos($input: VideoV2PaginationInput) {
    videosV2Pagination(input: $input) {
      docs {
        ${FEED_VIDEO_FIELDS}
      }
    }
  }
`;

// Admin page "Classements": the videos of a ranking (ranking + videoIds), with what the video drawer needs.
export const ADMIN_RANKING_VIDEOS = `
  query adminRankingVideos($input: VideoV2PaginationInput) {
    videosV2Pagination(input: $input) {
      docs {
        codeshare
        createdAt
        id
        description
        kills
        mode
        likes
        gameName
        comments
        thumbnail
        thumbnailInPng
        top
        path
        short
        platform
        user {
          ${CREATOR_FIELDS}
          rank
        }
        userId
        views
        weapon
      }
    }
  }
`;

// Videos of a player, newest first (userId + offset without random = profile mode).
export const PROFILE_VIDEOS = `
  query profileVideos($input: VideoV2PaginationInput) {
    videosV2Pagination(input: $input) {
      docs {
        id
        codeshare
        description
        gameName
        likes
        path
        pinnedAt
        thumbnail
        top
        views
        weeklyRanking
      }
      hasNextPage
      totalDocs
    }
  }
`;

// Favoris tab of the owner profile: no userId, the server always returns the connected user's favorites.
export const FAVORITE_VIDEOS = `
  query favoriteVideos($input: FavoriteVideoPaginationInput) {
    favoriteVideosPagination(input: $input) {
      docs {
        id
        codeshare
        description
        gameName
        likes
        path
        thumbnail
        top
        views
      }
      hasNextPage
      totalDocs
    }
  }
`;

export const LIKED_VIDEOS = `
  query likedVideos($input: LikedVideoPaginationInput) {
    likedVideosPagination(input: $input) {
      docs {
        id
        codeshare
        description
        gameName
        likes
        path
        thumbnail
        top
        views
      }
      hasNextPage
      totalDocs
    }
  }
`;

// The videos of a profile, his favorites or the videos he liked, played in the feed (every field of the feed).
export const PROFILE_FEED_VIDEOS = `
  query profileFeedVideos($input: VideoV2PaginationInput) {
    videosV2Pagination(input: $input) {
      docs {
        ${FEED_VIDEO_FIELDS}
      }
    }
  }
`;

export const FAVORITE_FEED_VIDEOS = `
  query favoriteFeedVideos($input: FavoriteVideoPaginationInput) {
    favoriteVideosPagination(input: $input) {
      docs {
        ${FEED_VIDEO_FIELDS}
      }
    }
  }
`;

export const LIKED_FEED_VIDEOS = `
  query likedFeedVideos($input: LikedVideoPaginationInput) {
    likedVideosPagination(input: $input) {
      docs {
        ${FEED_VIDEO_FIELDS}
      }
    }
  }
`;
